import React, { useRef, useEffect } from 'react';
import { DayColors, ColorOption } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ColorPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectColor: (colorKey: ColorOption) => void;
  currentDay: Date;
  targetElement: HTMLElement | null;
}

const ColorPopover: React.FC<ColorPopoverProps> = ({ isOpen, onClose, onSelectColor, currentDay, targetElement }) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node) && targetElement && !targetElement.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, targetElement]);

  if (!isOpen || !targetElement) return null;

  const colorOptions = Object.keys(DayColors) as ColorOption[];
  const rect = targetElement.getBoundingClientRect();

  const top = rect.bottom + window.scrollY + 5;
  const left = rect.left + window.scrollX;

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 bg-white p-3 rounded-lg shadow-xl border border-gray-200 w-72" // Aumentei um pouco a largura para acomodar o novo formato
      style={{ top: `${top}px`, left: `${left}px` }}
    >
      <h3 className="text-sm font-semibold mb-2 text-center text-gray-700">
        Alterar Dia: {format(currentDay, "dd/MM/yyyy", { locale: ptBR })}
      </h3>
      <div className="space-y-1.5">
        {colorOptions.map(colorKey => {
          const colorInfo = DayColors[colorKey];
          // Determina a cor do texto para contraste com o background do botão, não do círculo
          const buttonTextColor = colorInfo.background.includes('gray-400') || colorInfo.background.includes('blue-300') || colorInfo.background.includes('pink-500') ? 'text-black' : 'text-white';
          
          return (
            <button
              key={colorKey}
              onClick={() => onSelectColor(colorKey)}
              className={`w-full p-2 rounded-md ${colorInfo.background} ${buttonTextColor} hover:opacity-80 transition-opacity duration-150 text-left text-xs flex items-center justify-between`}
              title={`${colorInfo.meaning} - ${colorInfo.hours} horas`}
            >
              <div className="flex items-center">
                <span 
                  className={`w-3 h-3 rounded-full mr-2 border border-gray-300 ${colorInfo.background}`}
                  style={{ backgroundColor: colorInfo.background.startsWith('bg-') ? undefined : colorInfo.background }} // Para cores hex diretas se necessário
                ></span>
                <span className="font-medium">{colorInfo.meaning}</span>
              </div>
              <span className="text-xxs opacity-90">{colorInfo.hours} hs</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ColorPopover;

